import { useLanguage } from "context";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  CustomInput,
  CustomTextarea,
  FormDropdown,
  InputWithIcon,
  PrevNextButton,
  SpaceLocationMap,
} from "reusableComponents";
import React, { useEffect, useState } from "react";
import { selectAuthToken } from "store/auth/selectors";
import { getUsageOptions } from "services/filters.services";
import {
  fetchSpaceDataWithAuth,
  getPlaceTypes,
  submitStep1,
} from "services/addSpace.services";
import { adIcon, corporateIcon } from "assets/icons";
import { getErrorMessage } from "helpers/errorHandling";
import GoogleAutocompleteWrapper from "./GoogleAutocompleteWrapper";
import styles from "./styles.module.scss";
import { validateAddSpaceStep1 } from "helpers/addSpaceValidation";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useNavigate, useParams } from "react-router";

// Google Maps script loader for geocoding
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis"]')) {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    window.initGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
      } else {
        reject(new Error('Google Maps failed to load'));
      }
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
};

const Step1 = ({ onSubmit, lastStep, setLastStep }) => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { language } = useLanguage();
  const accessToken = useSelector(selectAuthToken);
  
  // State for lazy loading
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const [isLoadingMaps, setIsLoadingMaps] = useState(false);
  
  const [placeTypeOptions, setPlaceTypeOptions] = useState([]);
  const [placeUsageOptions, setPlaceUsageOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState([]);
  
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const floorOptions = [
    { value: "100", label: t("provide.step1.address.basement") },
    { value: "0", label: t("provide.step1.address.groundFloor") },
    ...Array.from({ length: 30 }, (_, index) => ({
      value: (index + 1).toString(),
      label: `${t("provide.step1.address.floor")} ${index + 1}`,
    })),
  ];
  
  const [formData, setFormData] = useState({
    address: {
      lat: null,
      lng: null,
      address: "",
      address2: "",
      city: "",
      country: "",
      postal_code: "",
      district: "",
      contry_code: "",
    },
  });

  // Load Google Maps on component mount
  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapsError("Google Maps API key is missing");
      return;
    }

    let isMounted = true;
    setIsLoadingMaps(true);

    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript(googleMapsApiKey);
        if (isMounted) {
          setMapsLoaded(true);
          setMapsError(null);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to load Google Maps:", error);
          setMapsError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoadingMaps(false);
        }
      }
    };

    loadMaps();

    return () => {
      isMounted = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchSpaceDataWithAuth(
          spaceId,
          accessToken,
          language
        );
        setFormData((prev) => {
          const updatedData = {
            ...data,
            place_id: data.id,
            place_usage_id: data.placeUsage?.id ? data.placeUsage?.id : "",
            place_type_id: data.placeType?.id ? data.placeType.id : "",
            floor: data.floor?.toString() ? data.floor.toString() : null,
          };

          return updatedData;
        });
        setLastStep(data.last_step || 1);
      } catch (error) {
        console.error("Error fetching space data:", error);
      }
    };

    if (spaceId) {
      fetchData();
    }
  }, [spaceId, accessToken, language, setLastStep]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [types, usages] = await Promise.all([
          fetchPlaceTypes(),
          fetchPlaceUsages(),
        ]);

        setPlaceTypeOptions(
          types.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );

        setPlaceUsageOptions(
          usages.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching types or usages:", error);
      }
    };

    const fetchPlaceTypes = async () => {
      const data = await getPlaceTypes(language, accessToken);
      return data;
    };

    const fetchPlaceUsages = async () => {
      const data = await getUsageOptions(language);
      return data;
    };

    if (language && accessToken) {
      fetchData();
    }
  }, [language, accessToken]);

  const handleDropdownChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (Object.keys(prev.address).includes(name)) {
        return {
          ...prev,
          address: {
            ...prev.address,
            [name]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleCheckboxChange = (key) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: !prevData[key],
    }));
  };

  const handleAddressSelect = async (address) => {
    if (!mapsLoaded) {
      alert("Google Maps is still loading. Please wait a moment.");
      return;
    }

    setLoading(true);

    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      const addressComponents = results[0].address_components;

      const city =
        addressComponents.find((c) => c.types.includes("locality"))
          ?.long_name || "";
      const country =
        addressComponents.find((c) => c.types.includes("country"))?.long_name ||
        "";
      const country_code =
        addressComponents.find((c) => c.types.includes("country"))
          ?.short_name || "";
      const postal_code =
        addressComponents.find((c) => c.types.includes("postal_code"))
          ?.long_name || "";
      const district =
        addressComponents.find((c) => c.types.includes("sublocality"))
          ?.long_name || "";

      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          address,
          lat: latLng.lat,
          lng: latLng.lng,
          city,
          country,
          country_code,
          postal_code,
          district,
        },
      }));
    } catch (error) {
      console.error("Error selecting address:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const validationSuccess = validateAddSpaceStep1(
      formData,
      errors,
      setErrors,
      t,
      loading,
      setLoading
    );
    if (!validationSuccess) {
      setLoading(false);
      return;
    }

    try {
      const response = await submitStep1(formData, language, accessToken);

      if (onSubmit) {
        onSubmit(response);
      }
      navigate(`/create-space/step2/${response.data.id}`);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return <div className={styles.errorMessage}>{error}</div>;
  }

  return (
    <div className={`${loading ? styles.loading : ""}`}>
      <PrevNextButton
        color="nav"
        onNext={handleSubmit}
        onPrev={() => navigate("/dashboard/spaces")}
        isIconOnly
      />
      
      {isLoadingMaps && (
        <div className={styles.mapsLoadingBanner}>
          <div className={styles.spinner}></div>
          <span>Loading Google Maps...</span>
        </div>
      )}
      
      {mapsError && (
        <div className={styles.mapsErrorBanner}>
          ⚠️ {mapsError} - Address autocomplete may not work properly
        </div>
      )}
      
      <h3 className={styles.subtitle}>{t("provide.step1.space.title")}</h3>
      <section className={`${styles.formSection} ${styles.formSection1}`}>
        <FormDropdown
          onSelect={(value) => handleDropdownChange("place_type_id", value)}
          selected={formData.place_type_id}
          options={placeTypeOptions}
          label={t("provide.step1.space.type")}
          required
          error={getErrorMessage("place_type_id", errors)}
        />
        <FormDropdown
          onSelect={(value) => handleDropdownChange("place_usage_id", value)}
          selected={formData.place_usage_id}
          options={placeUsageOptions}
          label={t("provide.step1.space.usage")}
          required
          error={getErrorMessage("place_usage_id", errors)}
        />
        <InputWithIcon
          icon={corporateIcon}
          label={t("provide.step1.space.name")}
          required
          value={formData.name || ""}
          handleChange={handleInputChange}
          name="name"
          error={getErrorMessage("name", errors)}
        />
      </section>
      
      <h3 className={styles.subtitle}>{t("provide.step1.address.title")}</h3>
      <section className={`${styles.formSection} ${styles.formSection2}`}>
        <div className={styles.inputsContainer}>
          <div className={styles.groupedItems}>
            <GoogleAutocompleteWrapper
              savedAddress={formData.address?.address}
              label={t("provide.step1.address.street")}
              labelAbove
              required
              apiKey={googleMapsApiKey}
              handleAddressSelect={handleAddressSelect}
              error={getErrorMessage("address", errors)}
              t={t}
            />

            <CustomInput
              label={t("provide.step1.address.city")}
              labelAbove
              required
              name="city"
              value={formData.address?.city || ""}
              handleChange={handleInputChange}
              error={getErrorMessage("city", errors)}
            />
            <CustomInput
              label={t("provide.step1.address.country")}
              labelAbove
              required
              name="country"
              value={formData.address?.country || ""}
              handleChange={handleInputChange}
              error={getErrorMessage("country", errors)}
            />
            <CustomInput
              label={t("provide.step1.address.district")}
              labelAbove
              name="district"
              value={formData.address?.district || ""}
              handleChange={handleInputChange}
              error={getErrorMessage("district", errors)}
            />
            <CustomInput
              label={t("provide.step1.address.zipCode")}
              labelAbove
              required
              name="postal_code"
              value={formData.address?.postal_code || ""}
              handleChange={handleInputChange}
              error={getErrorMessage("postal_code", errors)}
            />
          </div>

          <div className={styles.groupedItems}>
            <FormDropdown
              onSelect={(value) => handleDropdownChange("floor", value)}
              selected={formData.floor}
              options={floorOptions}
              label={t("provide.step1.address.floor")}
              labelAbove
              required
              error={getErrorMessage("floor", errors)}
            />
            <CustomInput
              label={t("provide.step1.address.accessCode")}
              labelAbove
              handleChange={handleInputChange}
              name="access_code"
              value={formData.access_code || ""}
              error={getErrorMessage("access_code", errors)}
            />
            <CustomInput
              label={t("provide.step1.address.intercom")}
              labelAbove
              handleChange={handleInputChange}
              name="interphone"
              value={formData.interphone || ""}
              error={getErrorMessage("interphone", errors)}
            />
          </div>
          
          <div className={styles.groupedItems}>
            <Checkbox
              onChange={() => handleCheckboxChange("elevator")}
              checked={formData.elevator || false}
              label={t("provide.step1.address.elevator")}
              isSquare
              iconType
            />
            <Checkbox
              checked={formData.accessibility || false}
              onChange={() => handleCheckboxChange("accessibility")}
              label={t("provide.step1.address.accessibility")}
              isSquare
              iconType
            />
          </div>
        </div>
        
        <div className={styles.mapContainer}>
          <SpaceLocationMap
            activeSpace={formData}
            setActiveSpace={setFormData}
            isMapVisible={true}
          />
        </div>
      </section>
      
      <h3 className={styles.subtitle}>{t("provide.step1.ad.title")}</h3>
      <section className={`${styles.formSection} ${styles.formSection3}`}>
        <div>
          <InputWithIcon
            icon={adIcon}
            label={t("provide.step1.ad.adTitle")}
            required
            value={formData.title || ""}
            handleChange={handleInputChange}
            name="title"
            error={getErrorMessage("title", errors)}
          />
        </div>
        <CustomTextarea
          label={t("provide.step1.ad.description")}
          name="description"
          placeholder={t("provide.step1.ad.description")}
          value={formData.description || ""}
          onChange={handleInputChange}
          example={t("provide.step1.ad.help")}
          required
          labelAbove
          error={getErrorMessage("description", errors)}
        />
      </section>
      
      <div className={styles.nextBtnContainer}>
        <PrevNextButton
          color="nav"
          isPrevDisabled={true}
          onNext={handleSubmit}
          disabled={loading || isLoadingMaps}
        />
      </div>
    </div>
  );
};

export default Step1;